import type { FC, MouseEventHandler } from "react";
import { useCallback, useMemo, useRef } from "react";

import styled from "styled-components";

import type { IDynamicFilterRule } from "@similarweb/pro-logic-components";
import { MultiIncludeExcludeFilter } from "@similarweb/pro-logic-components";
import { PopupClickContainer } from "@similarweb/ui-components";

import { useExpectedBacklinksParams } from "app/pages/digital-marketing/backlinks/shared/BacklinksUtils";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { Chip } from "./include-exclude.styled";
import type { IDynamicFilterRuleOverride } from "./include-exclude.types";
import { dynamicRulesFromQueryString, DynamicRulesToQueryString } from "./include-exclude.utils";

const MultiIncludeExcludeFilterWrapper = styled.div`
    max-height: 550px;
    overflow-y: auto;
`;

/*
The new Popover does not support MultiIncludeExcludeFilter
because of the dropdown component, the z-index is broken
*/
const BacklinksIncludeExcludeFilter: FC = () => {
    const { includeExclude } = useExpectedBacklinksParams();
    const translate = useTranslation();
    const popoverRef = useRef<PopupClickContainer>(null);

    const rulesFromQuery = useMemo(() => {
        return dynamicRulesFromQueryString(includeExclude, [
            {
                type: "include",
                fieldName: "UrlFrom",
                matchType: "partial",
                items: [],
            },
        ]);
    }, [includeExclude]);

    const includeExcludeOptions = useMemo(() => {
        return [
            {
                ruleProperty: "type",
                options: [
                    { id: "include", title: translate("global.include") },
                    { id: "exclude", title: translate("global.exclude") },
                ],
                buttonWidth: 100,
                width: 200,
            },
            {
                ruleProperty: "fieldName",
                options: [
                    { id: "UrlFrom", title: translate("backlinks.referralURL") },
                    { id: "UrlTo", title: translate("backlinks.targetURL") },
                    { id: "anchor", title: translate("backlinks.anchor") },
                ],
                buttonWidth: 140,
                width: 250,
            },
            {
                ruleProperty: "matchType",
                options: [
                    {
                        id: "partial",
                        title: translate("backlinks.partialMatch"),
                        description: translate("backlinks.multi.include.exclude.keywords.phrase.match.description"),
                    },
                    {
                        id: "exact",
                        title: translate("backlinks.exactMatch"),
                        description: translate("backlinks.multi.include.exclude.keywords.exact.match.description"),
                    },
                ],
                buttonWidth: 140,
                width: 200,
            },
        ];
    }, [translate]);

    const anyRulesApplied = useMemo(() => {
        return rulesFromQuery.some((rule) => rule.items.length > 0);
    }, [rulesFromQuery]);

    const handleOnApply = useCallback((rules?: IDynamicFilterRuleOverride[]) => {
        const rulesString = DynamicRulesToQueryString(rules);
        if (!rulesString) {
            void proNavigator.updateParams({ includeExclude: null }, { location: "replace" });
        }
        void proNavigator.updateParams({ includeExclude: rulesString }, { location: "replace" });
        popoverRef.current?.closePopup();
    }, []);

    const popoverContent = useCallback(() => {
        return (
            <MultiIncludeExcludeFilterWrapper>
                <MultiIncludeExcludeFilter
                    inputIconName="globe"
                    addRuleText={translate("multi.include.exclude.keywords.add.rule")}
                    autocompleteMaxItemsBannerText={translate(
                        "multi.include.exclude.keywords.max.keywords.in.rule.tip",
                        {
                            count: 20,
                        },
                    )}
                    chipsSeparatorText={translate("global.or")}
                    dropDownsOptions={includeExcludeOptions}
                    maxItemsPerRule={20}
                    initialRules={rulesFromQuery as unknown as IDynamicFilterRule[]}
                    maxItemsPerRuleBannerText={translate("multi.include.exclude.keywords.max.keywords.in.rule.tip", {
                        count: 20,
                    })}
                    maxRules={3}
                    applyButtonText={translate("global.add")}
                    maxRulesBannerText={translate("global.maxRules", { maxRules: 3 })}
                    onApply={(e) => handleOnApply(e as unknown as IDynamicFilterRuleOverride[])}
                    onCancelClick={() => handleOnApply()}
                    primaryButtonText={translate("global.apply")}
                    ruleSeparatorText={translate("global.and")}
                    secondaryButtonText={translate("global.clearFilters")}
                    allowEmptyRule={true}
                />
            </MultiIncludeExcludeFilterWrapper>
        );
    }, [handleOnApply, includeExcludeOptions, rulesFromQuery, translate]);

    const handleClearFilters: MouseEventHandler<HTMLSpanElement> = useCallback(() => {
        if (!anyRulesApplied) {
            return;
        }
        void proNavigator.updateParams({ includeExclude: "" }, { location: "replace" });
    }, [anyRulesApplied]);

    return (
        <PopupClickContainer
            config={{
                width: 700,
                enabled: true,
                placement: "ontop-left",
                cssClassContent: "multiIncludeExclude--Popup-container",
                cssClassContainer: "Popup-element-wrapper-triangle multiIncludeExclude--Popup-container",
                closeOnClickOut: false,
            }}
            appendTo="body"
            content={popoverContent}
            ref={popoverRef}
        >
            <Chip
                $isActive={anyRulesApplied}
                text={translate("multi.include.exclude.action.button.text")}
                onCloseItem={handleClearFilters}
                hideActionButton={!anyRulesApplied}
                customIcon={anyRulesApplied ? undefined : "arrow"}
            />
        </PopupClickContainer>
    );
};

export default BacklinksIncludeExcludeFilter;
